import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Container,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import AssignmentTurnedInRoundedIcon from "@mui/icons-material/AssignmentTurnedInRounded";
import AutoStoriesRoundedIcon from "@mui/icons-material/AutoStoriesRounded";
import BrushRoundedIcon from "@mui/icons-material/BrushRounded";
import CalculateRoundedIcon from "@mui/icons-material/CalculateRounded";
import ExtensionRoundedIcon from "@mui/icons-material/ExtensionRounded";
import HeadphonesRoundedIcon from "@mui/icons-material/HeadphonesRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import OndemandVideoRoundedIcon from "@mui/icons-material/OndemandVideoRounded";
import PsychologyAltRoundedIcon from "@mui/icons-material/PsychologyAltRounded";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import ScienceRoundedIcon from "@mui/icons-material/ScienceRounded";
import TouchAppRoundedIcon from "@mui/icons-material/TouchAppRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import Header from "../header_h/Header";
import Footer from "../footer/Footer";
import BreadcrumbsModulePage from "../breadcrumbs/BreadcrumbsModulePage";
import DownloadPdfButton from "../download_pdf_button/DownloadPdfButton";
import { modulesData } from "./modulesData";
import { getAuthorsBySubject, getModuleSections, getModulesByAuthor } from "../../api/subjects";
import "../class_page/class_page.css";
import "../selection_container/SelectionContainer.css";

const normalize = (v) => String(v ?? "").trim();
const lower = (v) => normalize(v).toLowerCase();
const hasMeaningfulHtml = (html) => {
  const value = normalize(html);
  if (!value) return false;
  if (/<(img|iframe|video|audio)\b/i.test(value)) return true;
  return value
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;|&#160;/gi, " ")
    .trim().length > 0;
};

const getLegacyModulesSafe = (data, id, subject, author) => {
  if (!data) return [];

  const idStr = normalize(id);
  const idNumStr = String(Number(idStr));
  const subj = normalize(subject);
  const auth = normalize(author);

  const direct = data?.[idStr]?.[subj]?.[auth] || data?.[idNumStr]?.[subj]?.[auth] || [];

  if (Array.isArray(direct) && direct.length) return direct;

  const classKeys = Object.keys(data || {});
  const classKey =
    classKeys.find((k) => normalize(k) === idStr) ||
    classKeys.find((k) => normalize(k) === idNumStr) ||
    classKeys.find((k) => lower(k) === lower(idStr)) ||
    classKeys.find((k) => lower(k) === lower(idNumStr));

  if (!classKey) return [];

  const subjObj = data[classKey] || {};
  const subjKeys = Object.keys(subjObj || {});
  const subjKey =
    subjKeys.find((k) => normalize(k) === subj) ||
    subjKeys.find((k) => lower(k) === lower(subj));

  if (!subjKey) return [];

  const authObj = subjObj[subjKey] || {};
  const authKeys = Object.keys(authObj || {});
  const authKey =
    authKeys.find((k) => normalize(k) === auth) ||
    authKeys.find((k) => lower(k) === lower(auth));

  if (!authKey) return [];

  const arr = authObj[authKey];
  return Array.isArray(arr) ? arr : [];
};

const normalizeSlug = (module) => {
  const slug = module?.moduleSlug ?? module?.path ?? "";
  return normalize(slug);
};

const slugifySection = (value) =>
  lower(value)
    .replace(/[^a-z0-9а-яіїєґ]+/gi, "-")
    .replace(/^-+|-+$/g, "");

const normalizeSection = (section, idx = 0) => {
  const title = normalize(section?.title);
  const sectionSlug = normalize(section?.sectionSlug || section?.slug) || slugifySection(title);

  return {
    sectionSlug,
    title,
    description: normalize(section?.description),
    iconKey: normalize(section?.iconKey) || "test",
    order: Number(section?.order ?? idx) || 0,
    enabled: section?.enabled !== false,
  };
};

const sectionIcon = (iconKey, idx) => {
  if (iconKey === "test") return <PsychologyAltRoundedIcon />;
  if (iconKey === "interactive") return <TouchAppRoundedIcon />;
  if (iconKey === "video") return <OndemandVideoRoundedIcon />;
  if (iconKey === "openBook") return <MenuBookRoundedIcon />;
  if (iconKey === "school") return <SchoolRoundedIcon />;
  if (iconKey === "reading") return <AutoStoriesRoundedIcon />;
  if (iconKey === "math") return <CalculateRoundedIcon />;
  if (iconKey === "science") return <ScienceRoundedIcon />;
  if (iconKey === "creative") return <BrushRoundedIcon />;
  if (iconKey === "puzzle") return <ExtensionRoundedIcon />;
  if (iconKey === "checklist") return <AssignmentTurnedInRoundedIcon />;
  if (iconKey === "audio") return <HeadphonesRoundedIcon />;

  const fallbackIcons = [
    <PsychologyAltRoundedIcon />,
    <TouchAppRoundedIcon />,
    <OndemandVideoRoundedIcon />,
    <MenuBookRoundedIcon />,
    <SchoolRoundedIcon />,
    <AutoStoriesRoundedIcon />,
    <CalculateRoundedIcon />,
    <ScienceRoundedIcon />,
    <BrushRoundedIcon />,
    <ExtensionRoundedIcon />,
    <AssignmentTurnedInRoundedIcon />,
    <HeadphonesRoundedIcon />,
  ];
  return fallbackIcons[idx % fallbackIcons.length];
};

const mergeModules = (legacy, mongo) => {
  const map = new Map();

  (Array.isArray(legacy) ? legacy : []).forEach((module, idx) => {
    const slug = normalizeSlug(module);
    if (!slug) return;
    map.set(slug, {
      moduleSlug: slug,
      title: normalize(module?.title),
      order: typeof module?.order === "number" ? module.order : idx,
      enabled: true,
      pdfPath: module?.pdfPath || null,
      pdfFileName: module?.pdfFileName || null,
      sectionSlug: normalize(module?.sectionSlug),
      sectionTitle: normalize(module?.sectionTitle),
      sectionDescription: normalize(module?.sectionDescription),
      sectionIconKey: normalize(module?.sectionIconKey),
      sectionOrder: typeof module?.sectionOrder === "number" ? module.sectionOrder : 0,
      __source: "legacy",
    });
  });

  (Array.isArray(mongo) ? mongo : []).forEach((module, idx) => {
    const slug = normalizeSlug(module);
    if (!slug) return;

    const prev = map.get(slug);
    map.set(slug, {
      moduleSlug: slug,
      title: normalize(module?.title ?? prev?.title),
      order:
        typeof module?.order === "number"
          ? module.order
          : typeof prev?.order === "number"
            ? prev.order
            : idx,
      enabled:
        typeof module?.enabled === "boolean"
          ? module.enabled
          : typeof prev?.enabled === "boolean"
            ? prev.enabled
            : true,
      pdfPath: module?.pdfPath ?? prev?.pdfPath ?? null,
      pdfFileName: module?.pdfFileName ?? prev?.pdfFileName ?? null,
      sectionSlug: normalize(module?.sectionSlug ?? prev?.sectionSlug),
      sectionTitle: normalize(module?.sectionTitle ?? prev?.sectionTitle),
      sectionDescription: normalize(module?.sectionDescription ?? prev?.sectionDescription),
      sectionIconKey: normalize(module?.sectionIconKey ?? prev?.sectionIconKey),
      sectionOrder:
        typeof module?.sectionOrder === "number"
          ? module.sectionOrder
          : typeof prev?.sectionOrder === "number"
            ? prev.sectionOrder
            : 0,
      __source: prev ? "merged" : "mongo",
    });
  });

  return Array.from(map.values())
    .filter((module) => module.enabled !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

const AuthorPage = ({ userName, userRoles }) => {
  const { id, subject, author } = useParams();
  const navigate = useNavigate();

  const [mongoModules, setMongoModules] = useState([]);
  const [authorSections, setAuthorSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSection, setSelectedSection] = useState("");
  const [authorInfo, setAuthorInfo] = useState(null);
  const [infoExpanded, setInfoExpanded] = useState(false);
  const [infoOverflow, setInfoOverflow] = useState(false);
  const infoContentRef = useRef(null);

  useEffect(() => {
    const content = infoContentRef.current;
    if (!content || !hasMeaningfulHtml(authorInfo?.infoHtml)) {
      setInfoOverflow(false);
      return undefined;
    }

    const measure = () => setInfoOverflow(content.scrollHeight > 66);
    measure();

    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    observer?.observe(content);
    window.addEventListener("resize", measure);

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [authorInfo?.infoHtml]);

  const legacyModules = useMemo(() => {
    return getLegacyModulesSafe(modulesData, id, subject, author);
  }, [id, subject, author]);

  useEffect(() => {
    let mounted = true;

    async function load() {
      if (!id || !subject || !author) return;
      setLoading(true);
      try {
        const [modulesResult, sectionsResult, authorsResult] = await Promise.allSettled([
          getModulesByAuthor(id, subject, author),
          getModuleSections(id, subject, author),
          getAuthorsBySubject(id, subject),
        ]);
        if (!mounted) return;

        setMongoModules(
          modulesResult.status === "fulfilled" && Array.isArray(modulesResult.value)
            ? modulesResult.value
            : []
        );
        setAuthorSections(
          sectionsResult.status === "fulfilled" && Array.isArray(sectionsResult.value)
            ? sectionsResult.value.map(normalizeSection).filter((section) => section.sectionSlug && section.enabled !== false)
            : []
        );
        const authors = authorsResult.status === "fulfilled" && Array.isArray(authorsResult.value)
          ? authorsResult.value
          : [];
        setAuthorInfo(authors.find((item) => normalize(item?.authorSlug) === normalize(author)) || null);
      } catch (e) {
        if (mounted) {
          setMongoModules([]);
          setAuthorSections([]);
          setAuthorInfo(null);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [id, subject, author]);

  const modules = useMemo(() => {
    return mergeModules(legacyModules, mongoModules);
  }, [legacyModules, mongoModules]);

  useEffect(() => {
    setSelectedSection("");
    setInfoExpanded(false);
  }, [id, subject, author]);

  const sections = useMemo(() => {
    const map = new Map();

    authorSections.forEach((section) => {
      if (!section.sectionSlug || !section.title) return;
      map.set(section.sectionSlug, {
        ...section,
        slug: section.sectionSlug,
        modules: [],
      });
    });

    modules.forEach((module) => {
      const moduleTitle = normalize(module.sectionTitle);
      const slug = normalize(module.sectionSlug) || slugifySection(moduleTitle);
      const prev = map.get(slug);
      const title = prev?.title || moduleTitle;
      if (!slug || !title) return;

      map.set(slug, {
        slug,
        title,
        description: prev?.description || normalize(module.sectionDescription),
        iconKey: prev?.iconKey || normalize(module.sectionIconKey) || "test",
        order:
          typeof prev?.order === "number"
            ? prev.order
            : typeof module.sectionOrder === "number"
            ? module.sectionOrder
            : 0,
        modules: [...(prev?.modules || []), module],
      });
    });

    const unsectioned = modules.filter((module) => {
      const title = normalize(module.sectionTitle);
      const slug = normalize(module.sectionSlug) || slugifySection(title);
      return !slug || !title;
    });

    const hasSectionedModules = Array.from(map.values()).some((section) => section.modules.length > 0);

    if (hasSectionedModules && unsectioned.length > 0) {
      map.set("__other", {
        slug: "__other",
        title: "Інші модулі",
        description: "Модулі без окремої секції",
        order: 9999,
        modules: unsectioned,
      });
    }

    return Array.from(map.values())
      .filter((section) => section.modules.length > 0)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [authorSections, modules]);

  const visibleModules = useMemo(() => {
    if (!sections.length || !selectedSection) return modules;
    return sections.find((section) => section.slug === selectedSection)?.modules || [];
  }, [modules, sections, selectedSection]);

  const selectedSectionMeta = useMemo(() => {
    if (!selectedSection) return null;
    return sections.find((section) => section.slug === selectedSection) || null;
  }, [sections, selectedSection]);

  const showSectionPicker = false;

  return (
    <>
      <Header userName={userName} userRoles={userRoles} />
      <BreadcrumbsModulePage />

      <Container maxWidth="md" className="catalog-page">
        {hasMeaningfulHtml(authorInfo?.infoHtml) && (
          <Paper
            variant="outlined"
            sx={{
              mb: 1.5,
              p: { xs: 1.25, sm: 1.5 },
              borderColor: "primary.main",
              background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}12, ${theme.palette.background.paper} 55%)`,
            }}
          >
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
              <Box sx={{ display: "flex", p: 0.75, borderRadius: "50%", color: "primary.contrastText", bgcolor: "primary.main" }}>
                <InfoOutlinedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="overline" color="primary.main" fontWeight={800} lineHeight={1.4}>
                  Про е-додаток
                </Typography>
                {authorInfo.infoHtml && (
                  <>
                    <Collapse in={infoExpanded || !infoOverflow} collapsedSize={64}>
                      <Box
                        ref={infoContentRef}
                        className="author-info-html"
                        dangerouslySetInnerHTML={{ __html: authorInfo.infoHtml }}
                        sx={{ mt: 0.5, color: "text.secondary", overflowWrap: "anywhere", "& > :first-of-type": { mt: 0 }, "& > :last-child": { mb: 0 } }}
                      />
                    </Collapse>
                    {infoOverflow && (
                      <Button
                        size="small"
                        onClick={() => setInfoExpanded((current) => !current)}
                        endIcon={<ExpandMoreRoundedIcon sx={{ transform: infoExpanded ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />}
                        sx={{ mt: 0.5, minHeight: 28, py: 0 }}
                      >
                        {infoExpanded ? "Згорнути" : "Докладніше"}
                      </Button>
                    )}
                  </>
                )}
              </Box>
            </Box>
          </Paper>
        )}
        <Box className="catalog-page__heading">
          <Typography variant="h4" component="h1">
            Модулі
          </Typography>
          <Typography color="text.secondary">
            Оберіть модуль для проходження або завантажте PDF, якщо він доступний.
          </Typography>
        </Box>

        {showSectionPicker ? (
          <Paper className="module-type-panel" variant="outlined">
            <Typography variant="h5" component="h2">
              Оберіть тип завдань
            </Typography>
            <List disablePadding className="module-type-list">
              {sections.map((section, idx) => (
                <ListItemButton
                  key={section.slug}
                  className="module-type-list__item"
                  onClick={() => setSelectedSection(section.slug)}
                >
                  <ListItemIcon>{sectionIcon(section.iconKey, idx)}</ListItemIcon>
                  <ListItemText primary={section.title} secondary={section.description} />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        ) : (
          <Paper className="catalog-panel" variant="outlined">
            {selectedSection && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: { xs: "flex-start", sm: "center" },
                  justifyContent: "space-between",
                  gap: 1.5,
                  mb: 2,
                }}
              >
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Обраний тип
                  </Typography>
                  <Typography variant="h6" fontWeight={800}>
                    {selectedSectionMeta?.title || "Модулі"}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackRoundedIcon />}
                  onClick={() => setSelectedSection("")}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  До типів завдань
                </Button>
              </Box>
            )}

            {visibleModules.length > 0 ? (
              <List disablePadding className="catalog-list">
                {visibleModules.map((module) => (
                  <Box key={module.moduleSlug} className="module-list-row">
                    <ListItemButton
                      className="catalog-list__item"
                      onClick={() =>
                        navigate(`/class/${id}/${subject}/${author}/modules/${module.moduleSlug}/tasks`)
                      }
                    >
                      <ListItemText primary={module.title} />
                      <ChevronRightRoundedIcon />
                    </ListItemButton>

                    {module.pdfPath && (
                      <DownloadPdfButton
                        pdfPath={module.pdfPath}
                        pdfFileName={module.pdfFileName}
                      />
                    )}
                  </Box>
                ))}
              </List>
            ) : (
              <Box className="catalog-state">
                {loading && <CircularProgress size={28} />}
                <Typography>
                  {loading ? "Завантаження..." : "Модулі не знайдені"}
                </Typography>
              </Box>
            )}
          </Paper>
        )}
      </Container>
      <Footer />
    </>
  );
};

export default AuthorPage;
